import { FC, useCallback, useEffect, useMemo } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box } from '@altermaind/web/ui';
import { Form } from '@/_shared/components/Form';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useNotification } from '@/_shared/hooks/useNotification';
import { getEmployeesImportCall } from '@/people/state';
import { PAGE_SIZE_IMPORTED_EMPLOYEES } from '@/people/state/constants';

enum PROFILE_TYPE {
  COMPLETED = 'completed',
  INCOMPLETED = 'incompleted',
  ALL = 'all',
}

export const FilterImportedProfiles: FC = () => {
  const t = useTranslations('configuration.hr.verifyData.dialogEditImportedProfiles.filters');
  const sharedT = useTranslations();
  const { notify } = useNotification();
  const { closeDialog: closeDialogFilters } = useDialog('filters');
  const {
    execute: executeGetEmployeesImport,
    fullRequest: { payload: filters },
  } = getEmployeesImportCall.hook();

  const {
    handleSubmit,
    control,
    watch,
    formState: { isSubmitted },
  } = useForm({
    defaultValues: {
      completed: PROFILE_TYPE.ALL,
    },
    mode: 'onChange',
  });

  const fieldValues = watch();

  const onSubmit: SubmitHandler<FieldValues> = useCallback(() => {
    executeGetEmployeesImport({
      ...filters,
      complete:
        fieldValues.completed === 'all'
          ? undefined
          : fieldValues.completed === PROFILE_TYPE.COMPLETED,
      start: 0,
      length: PAGE_SIZE_IMPORTED_EMPLOYEES,
      successSaga: () => {
        notify(sharedT('success.genericApiSuccess'), 'success');
        closeDialogFilters();
      },
      errorSaga: () => {
        notify(sharedT('errors.genericApiError'), 'error');
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executeGetEmployeesImport, filters, fieldValues.completed, closeDialogFilters]);

  const radioItems = useMemo(() => {
    return [
      {
        label: t('importedProfileCompleted'),
        value: PROFILE_TYPE.COMPLETED,
        index: 0,
        selected: !!filters?.complete,
      },
      {
        label: t('importedProfileIncompleted'),
        value: PROFILE_TYPE.INCOMPLETED,
        index: 1,
        selected: filters?.complete === false,
      },
      {
        label: t('importedProfileAll'),
        value: PROFILE_TYPE.ALL,
        index: 2,
        selected: !filters?.complete && filters?.complete !== false,
      },
    ];
  }, [filters.complete, t]);

  useEffect(() => {
    // first run
    if (!isSubmitted && fieldValues?.completed === PROFILE_TYPE.ALL) {
      return;
    }
    handleSubmit(onSubmit)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValues.completed]);

  return (
    <Box>
      <Box display='flex' flexDirection='column'>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box>
            <Form.FormRadio
              zIndex={9999}
              // @ts-ignore
              control={control}
              name='completed'
              items={radioItems}
              radioItemAlign='vertical'
            />
          </Box>
        </form>
      </Box>
    </Box>
  );
};
